import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { cancelLeave } from '../../api/leaves'
import { keepLeaveAfterBlackout } from '../../api/blackouts'
import { Modal } from '../primitives/Modal'
import { fmtDateRange } from '../../lib/format'
import { flags } from '../../lib/feature-flags'

interface BlackoutResetBannerProps {
  leaveId: string
  status: string
  pendingReason: string | null | undefined
  startDate: string
  endDate: string
  /** Optional callback after the user successfully keeps or cancels — lets
   *  the parent dismiss the banner / refresh siblings. */
  onResolved?: () => void
}

/**
 * Action card surfaced on a leave whose ``pendingReason === 'blackout_reset'``.
 * Two paths:
 *  - Keep: leave stays PENDING but the reason tag clears, manager queue
 *    picks it up, manager is DM'd. No balance change.
 *  - Cancel: leave → CANCELLED via the existing /cancel endpoint, pending
 *    balance refunded.
 *
 * Renders nothing if the kill-switch flag is off or the leave isn't tagged.
 */
export function BlackoutResetBanner({
  leaveId,
  status,
  pendingReason,
  startDate,
  endDate,
  onResolved,
}: BlackoutResetBannerProps): JSX.Element | null {
  const queryClient = useQueryClient()
  const [confirmCancel, setConfirmCancel] = useState(false)

  const visible =
    flags.blackoutRequesterReconsider &&
    status === 'pending' &&
    pendingReason === 'blackout_reset'

  const keepMutation = useMutation({
    mutationFn: () => keepLeaveAfterBlackout(leaveId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['leaves'] })
      void queryClient.invalidateQueries({ queryKey: ['leave', leaveId] })
      onResolved?.()
    },
  })

  const cancelMutation = useMutation({
    mutationFn: () => cancelLeave(leaveId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['leaves'] })
      void queryClient.invalidateQueries({ queryKey: ['leave', leaveId] })
      void queryClient.invalidateQueries({ queryKey: ['balances'] })
      setConfirmCancel(false)
      onResolved?.()
    },
  })

  if (!visible) return null

  const busy = keepMutation.isPending || cancelMutation.isPending

  return (
    <div
      data-testid="blackout-reset-banner"
      data-leave-id={leaveId}
      role="region"
      aria-label="Blackout reset — decision required"
      style={{
        background: 'var(--warning-bg, #fef3c7)',
        border: '1px solid var(--warning-line, #fcd34d)',
        color: 'var(--warning-fg, #92400e)',
        borderRadius: 12,
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 700 }}>
        Your leave needs a decision
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--text-2)' }}>
        Your approved leave on{' '}
        <strong style={{ color: 'var(--text)' }}>
          {fmtDateRange(startDate, endDate)}
        </strong>{' '}
        is now inside a blackout window. Choose <strong>Keep</strong> and your
        approver will re-approve under the new policy, or{' '}
        <strong>Cancel</strong> to release the days back to your balance.
      </div>

      {keepMutation.isError ? (
        <div role="alert" style={{ fontSize: 12, color: 'var(--danger-fg)' }}>
          Could not save your choice. Please try again.
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button
          type="button"
          data-testid="blackout-reset-cancel"
          onClick={() => setConfirmCancel(true)}
          disabled={busy}
          style={{
            padding: '8px 14px',
            background: 'var(--surface)',
            color: 'var(--text-2)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            fontWeight: 600,
            fontSize: 13,
            cursor: busy ? 'not-allowed' : 'pointer',
          }}
        >
          Cancel request
        </button>
        <button
          type="button"
          data-testid="blackout-reset-keep"
          onClick={() => keepMutation.mutate()}
          disabled={busy}
          style={{
            padding: '8px 14px',
            background: busy ? 'var(--border-strong)' : 'var(--steel-blue)',
            color: 'var(--surface)',
            border: 'none',
            borderRadius: 8,
            fontWeight: 600,
            fontSize: 13,
            cursor: busy ? 'not-allowed' : 'pointer',
          }}
        >
          {keepMutation.isPending ? 'Keeping…' : 'Keep request'}
        </button>
      </div>

      <Modal
        open={confirmCancel}
        onClose={() => setConfirmCancel(false)}
        title="Cancel this leave?"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--text-2)', lineHeight: 1.5 }}>
            Cancelling will release the days back to your balance immediately.
            You'll need to file a new request if you want this leave back —
            and the blackout window will apply.
          </p>
          {cancelMutation.isError ? (
            <div
              role="alert"
              style={{
                padding: '10px 12px',
                background: 'var(--danger-bg)',
                color: 'var(--danger-fg)',
                border: '1px solid var(--danger-line)',
                borderRadius: 8,
                fontSize: 13,
              }}
            >
              {cancelMutation.error instanceof Error
                ? cancelMutation.error.message
                : 'Cancel failed'}
            </div>
          ) : null}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => setConfirmCancel(false)}
              disabled={cancelMutation.isPending}
              style={{
                padding: '8px 14px',
                background: 'var(--surface)',
                color: 'var(--text-2)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                fontWeight: 600,
                cursor: cancelMutation.isPending ? 'not-allowed' : 'pointer',
              }}
            >
              Keep open
            </button>
            <button
              type="button"
              data-testid="blackout-reset-confirm-cancel"
              onClick={() => cancelMutation.mutate()}
              disabled={cancelMutation.isPending}
              style={{
                padding: '8px 14px',
                background: cancelMutation.isPending
                  ? 'var(--border-strong)'
                  : 'var(--danger-bg)',
                color: cancelMutation.isPending ? 'var(--surface)' : 'var(--danger-fg)',
                border: '1px solid var(--danger-line)',
                borderRadius: 8,
                fontWeight: 600,
                cursor: cancelMutation.isPending ? 'not-allowed' : 'pointer',
              }}
            >
              {cancelMutation.isPending ? 'Cancelling…' : 'Yes, cancel'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
